import { Edit2, Trash2, MapPin } from 'lucide-react';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { Warehouse } from '../types/warehouses.types';

interface WarehousesTableProps {
  items: Warehouse[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  onEdit: (item: Warehouse) => void;
  onDelete: (item: Warehouse) => void;
  onPageChange: (page: number) => void;
}

export function WarehousesTable({
  items,
  total,
  page,
  pageSize,
  isLoading,
  onEdit,
  onDelete,
  onPageChange,
}: WarehousesTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (isLoading) {
    return (
      <div className="p-8 text-center space-y-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-slate-500">
        <MapPin className="w-12 h-12 mb-4 text-slate-300" />
        <p className="sm:text-sm text-xs">Không tìm thấy dữ liệu kho bãi.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto min-h-0 relative">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-1/4">Tên kho</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32">Mã kho</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-1/3">Vị trí</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32">Trạng thái</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-24 text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900 sm:text-sm text-xs">{item.name}</div>
                </td>
                <td className="px-4 py-3 sm:text-sm text-xs text-slate-600 font-mono">
                  {item.code || '-'}
                </td>
                <td className="px-4 py-3 sm:text-sm text-xs text-slate-600 truncate max-w-[200px]" title={item.location || ''}>
                  {item.location || '-'}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                    item.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'
                  }`}>
                    {item.is_active ? 'Hoạt động' : 'Đã đóng'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <AuthButton
                    module="inventory.warehouses"
                    action="update"
                    behavior="hide"
                    type="button"
                    onClick={() => onEdit(item)}
                    className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </AuthButton>
                  <AuthButton
                    module="inventory.warehouses"
                    action="delete"
                    behavior="hide"
                    type="button"
                    onClick={() => onDelete(item)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </AuthButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white">
        <div className="sm:text-sm text-xs text-slate-500">
          Hiển thị <span className="font-medium">{(page - 1) * pageSize + 1}</span> - <span className="font-medium">{Math.min(page * pageSize, total)}</span> trong số <span className="font-medium">{total}</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="px-3 py-1 border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Trước
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-1 border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Sau
          </button>
        </div>
      </div>
    </div>
  );
}
